// Control valve vertex schema
// A valve that can control flow or pressure

import { z } from 'zod';
import { BaseVertexSchema } from '../base/vertex';
import { ValueWithUnitSchema } from '../base/common';

/**
 * Valve Operation Mode Configurations
 * ADR-0006: Operating Mode Configuration Pattern
 * 
 * Different modes require different value structures.
 * Flat structure - properties at config level.
 */

// Mode: Flow (control flow rate)
const ValveFlowVolumetricConfigSchema = z.object({
  mode: z.literal('flow_volumetric'),
  pressure_type: z.literal('total'),
  value: z.number(),
  unit: z.string(), // e.g., "m³/s"
});

const ValveFlowMassConfigSchema = z.object({
  mode: z.literal('flow_mass'),
  pressure_type: z.literal('total'),
  value: z.number(),
  unit: z.string(), // e.g., "kg/s"
});

// Mode: Pressure (control pressure)
const ValvePressureDifferentialConfigSchema = z.object({
  mode: z.literal('pressure_differential'),
  value: z.number(),
  unit: z.string(), // e.g., "Pa g", "Pa"
  // "static" ⇒ the setpoint is a static pressure and the solver converts it to
  // total via the adjacent pipe's velocity head; "total" (default) ⇒ verbatim.
  pressure_type: z.enum(['total', 'static']).optional(),
});

const ValvePressureInletConfigSchema = z.object({
  mode: z.literal('pressure_inlet'),
  value: z.number(),
  unit: z.string(), // e.g., "Pa g", "Pa"
  pressure_type: z.enum(['total', 'static']).optional(),
});

const ValvePressureOutletConfigSchema = z.object({
  mode: z.literal('pressure_outlet'),
  value: z.number(),
  unit: z.string(), // e.g., "Pa g", "Pa"
  pressure_type: z.enum(['total', 'static']).optional(),
});

/**
 * Mode: Fixed flow coefficient (Kv or Cv)
 */
const ValveFixedFlowCoefficientSchema = z.object({
  mode: z.literal('fixed_flow_coefficient'),
  value: z.number(),
  unit: z.string(), // "Kv" or "Cv"
});

// Temperature control: the valve modulates flow based on the linked
// device's measured temperature, not a direct numeric setpoint at the
// valve. Desktop emits only { mode, linked_device } (renamed to
// linked_device_id by the transformer); there's no value/unit on the
// valve itself. Calc-engine doesn't yet have a handler for this mode
// (see parity-remediation-plan.md G7); when one is added, value/unit
// may still remain optional or unused.
const ValveTemperatureControlConfigSchema = z.object({
  mode: z.literal('temperature_control'),
  value: z.number().optional(),
  unit: z.string().optional(),
  linked_device_id: z.string().optional(),
});

/**
 * Mode: valve_position — Cv interpolated from `valve_data.curve[]` at the
 * given position. The calc-engine applies the full ISA-75.01 liquid
 * sizing model (Fp + FR + SumK) using fields from `valve_data` (curve, Fd,
 * nominal_size). Previously this mode was silently downconverted to
 * `fixed_flow_coefficient` with the un-sized `estimation_data.flow_coefficient`
 * value (which is 0 for most fixtures) — see [[valve-position-fcv-cv-interpolation-diverges-solver]].
 */
const ValvePositionConfigSchema = z.object({
  mode: z.literal('valve_position'),
  value: z.number(), // 0..100 (% open) when unit='%', or degrees if unit='deg'
  unit: z.enum(['%', 'deg']),
});

export const ValveOperationConfigSchema = z.discriminatedUnion('mode', [
  ValveFlowVolumetricConfigSchema,
  ValveFlowMassConfigSchema,
  ValvePressureDifferentialConfigSchema,
  ValvePressureInletConfigSchema,
  ValvePressureOutletConfigSchema,
  ValveFixedFlowCoefficientSchema,
  ValveTemperatureControlConfigSchema,
  ValvePositionConfigSchema,
]);

export type ValveOperationConfig = z.infer<typeof ValveOperationConfigSchema>;

/**
 * Control Valve Vertex Schema
 * A valve that can control flow or pressure
 * 
 * Uses nested config pattern (ADR-0006) because different modes require
 * different value structures
 * 
 * @property elevation - Height of the valve (for pressure calculations)
 */
// ============================================================================
// v2: Valve Data Source (ISA/IEC 60534 valve sizing data)
// ============================================================================

const ValveBodyTypeSchema = z.enum([
  'not_set', 'ball', 'butterfly_centered_shaft', 'butterfly_eccentric_shaft',
  'globe_angle', 'globe_double_port', 'globe_single_port', 'globe_small_flow_trim', 'rotary',
]);

const ValveTrimTypeSchema = z.enum([
  'not_set', 'three_v_port_plug', 'four_v_port_plug', 'six_v_port_plug',
  'sixty_equal_diameter_hole', 'one_twenty_equal_diameter_hole',
  'aligned_60_degrees', 'characterized_cage_4_port', 'contoured_plug',
  'eccentric_conical_plug', 'eccentric_spherical_plug', 'flat_seat_short_travel',
  'fluted_vane_70_degrees', 'full_bore_70_degrees', 'offset_seat_70_degrees',
  'offset_seat_90_degrees', 'ported_plug', 'segmented_ball',
  'swing_through_60_degrees', 'swing_through_70_degrees',
  'venturi', 'v_notch', 'wing_guided',
]);

const ValveFlowDirectionSchema = z.enum([
  'flow_none', 'flow_open', 'flow_close', 'flow_outward', 'flow_inward', 'flow_either',
]);

const ValveEquationTypeSchema = z.enum([
  'not_set', 'linear_standard', 'linear_modified',
  'equal_percentage_standard', 'equal_percentage_modified', 'quick_opening',
]);

const ValveCurvePointSchema = z.object({
  percent_position: z.number(),
  angle_position: z.number(),
  flow_coefficient: z.number().optional(),
  /** Liquid pressure recovery factor (ISA/IEC 60534) */
  Fl: z.number().optional(),
  /** Pressure drop ratio factor (ISA/IEC 60534) */
  xT: z.number().optional(),
});

const ValveEstimatorDataSchema = z.object({
  body_type: ValveBodyTypeSchema,
  trim_type: ValveTrimTypeSchema,
  flow_direction: ValveFlowDirectionSchema,
  equation_type: ValveEquationTypeSchema,
  valve_position: z.number(),
  valve_position_is_degrees: z.boolean(),
  flow_coefficient: z.number(),
  flow_coefficient_is_Kv: z.boolean(),
});

export const ValveDataSourceSchema = z.object({
  data_source: z.enum(['undefined', 'user_curve_percent', 'user_curve_angle']),
  nominal_size: ValueWithUnitSchema.optional(),
  pipe_size_units: z.string().optional(),
  flow_coefficient_unit: z.string().optional(),
  curve: z.array(ValveCurvePointSchema),
  /** Valve style modifier Fd (ISA/IEC 60534) */
  Fd: z.number(),
  estimation_data: ValveEstimatorDataSchema,
  has_xT_data: z.boolean(),
});

export type ValveDataSource = z.infer<typeof ValveDataSourceSchema>;

// ============================================================================
// Control Valve Vertex
// ============================================================================

export const ControlValveVertexSchema = BaseVertexSchema.extend({
  type: z.literal('control_valve'),
  elevation: ValueWithUnitSchema,
  operation: ValveOperationConfigSchema,

  // v2 additions
  valve_data: ValveDataSourceSchema.optional(),
  min_pressure_drop: ValueWithUnitSchema.optional(),
  max_pressure_drop: ValueWithUnitSchema.optional(),
});

export type ControlValveVertex = z.infer<typeof ControlValveVertexSchema>;

